import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  ChevronDown,
  Info,
  ScanLine,
  TestTube2,
  UploadCloud,
} from "lucide-react-native";
import {
  Pressable,
  ScrollView,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";
import {
  AnchoredSelectMenu,
  type AnchoredSelectMenuItem,
  Dialog,
  DialogActions,
  DialogHeader,
  DialogIcon,
  DialogPrimaryButton,
  DialogSecondaryButton,
  IconButton,
  MoonDateField,
  MoonFieldInput,
  MoonFormField,
  colors,
  typography,
  useToast,
} from "@louise/moon-ui";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { useTranslations } from "@src/packages/i18n";
import type { DocumentUploadSaveResult } from "@src/packages/document-vault/DocumentUploadModal";
import FactSheetTabControl, {
  type FactSheetTab,
} from "../../../components/FactSheetTabControl";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
const MONITORING_DIALOG_ILLUSTRATION =
  require("@src/apps/moon/assets/images/monitoring-dialog-illustration.png") as number;

export const MONITORING_UNIT_DEFAULTS = {
  estradiol: "pg/mL",
  lh: "mIU/mL",
  fsh: "mIU/mL",
  progesterone: "ng/mL",
} as const;

export type MonitoringEntryDraft = {
  leftSizes: string;
  rightSizes: string;
  leftCount: string;
  rightCount: string;
  liningThickness: string;
  estradiol: string;
  estradiolUnit: string;
  lh: string;
  lhUnit: string;
  fsh: string;
  fshUnit: string;
  progesterone: string;
  progesteroneUnit: string;
  notes: string;
};

export type MonitoringPreviousResult = {
  date: string;
  leftSizes: string;
  rightSizes: string;
  leftCount: string;
  rightCount: string;
  liningThickness: string;
  estradiol: string;
  estradiolUnit: string;
  lh: string;
  lhUnit: string;
  progesterone: string;
  progesteroneUnit: string;
};

type DraftKey = keyof MonitoringEntryDraft;
type ValidationErrors = Partial<Record<DraftKey | "date" | "form", string>>;
type MonitoringSection = "ultrasound" | "bloodwork";

type MonitoringEntryModalProps = {
  open: boolean;
  dateISO: string;
  current: MonitoringEntryDraft;
  previous: MonitoringPreviousResult | null;
  onClose: () => void;
  onDateChange?: (dateISO: string) => void;
  onSave: (
    draft: MonitoringEntryDraft,
    sourceDocumentIds: string[],
  ) => Promise<void>;
  onUploadReport?: MonitoringUploadRequest;
};

export type MonitoringUploadRequest = (
  dateISO: string,
  onMonitoringExtracted: (data: Record<string, unknown>) => void,
  onDocumentSaved?: (result: DocumentUploadSaveResult) => void,
) => void;

const MONITORING_VALUE_KEYS: DraftKey[] = [
  "leftSizes",
  "rightSizes",
  "leftCount",
  "rightCount",
  "liningThickness",
  "estradiol",
  "lh",
  "fsh",
  "progesterone",
  "notes",
];

const UNIT_OPTIONS: Record<
  "estradiol" | "lh" | "fsh" | "progesterone",
  { label: string; value: string }[]
> = {
  estradiol: [
    { label: "pg/mL", value: "pg/mL" },
    { label: "pmol/L", value: "pmol/L" },
  ],
  lh: [
    { label: "mIU/mL", value: "mIU/mL" },
    { label: "IU/L", value: "IU/L" },
  ],
  fsh: [
    { label: "mIU/mL", value: "mIU/mL" },
    { label: "IU/L", value: "IU/L" },
  ],
  progesterone: [
    { label: "ng/mL", value: "ng/mL" },
    { label: "nmol/L", value: "nmol/L" },
  ],
};

const parseNumberList = (raw: string): number[] =>
  raw
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean)
    .map(Number)
    .filter((value) => Number.isFinite(value));

function validateNumber(
  value: string,
  max: number,
  label: string,
): string | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > max) {
    return `${label} must be between 0 and ${max}.`;
  }
  return null;
}

function validateSizeList(value: string, label: string): string | null {
  if (!value.trim()) return null;
  const tokens = value.split(",").map((token) => token.trim());
  if (tokens.some((token) => !token || !/^\d+(\.\d+)?$/.test(token))) {
    return `${label} must use comma-separated numbers.`;
  }
  if (parseNumberList(value).some((size) => size < 0 || size > 50)) {
    return `${label} must be between 0 and 50 mm.`;
  }
  return null;
}

function formatDateLabel(dateISO: string): string {
  const date = new Date(`${dateISO}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateISO;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function formatISODate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function stringValue(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => stringValue(item))
      .filter(Boolean)
      .join(", ");
  }
  return "";
}

function monitoringValuesFromOCR(
  data: Record<string, unknown>,
): Partial<MonitoringEntryDraft> {
  const valueFor = (...keys: string[]) => {
    for (const key of keys) {
      const value = stringValue(data[key]);
      if (value) return value;
    }
    return "";
  };
  const results = Array.isArray(data.results)
    ? data.results.filter(
        (result): result is Record<string, unknown> =>
          !!result && typeof result === "object",
      )
    : [];
  const resultFor = (pattern: RegExp) => {
    const result = results.find((item) =>
      pattern.test(stringValue(item.marker)),
    );
    return result
      ? {
          value: stringValue(result.value),
          unit: stringValue(result.unit),
        }
      : { value: "", unit: "" };
  };

  const estradiol = resultFor(/estradiol|^e2$/i);
  const lh = resultFor(/^lh$/i);
  const fsh = resultFor(/^fsh$/i);
  const progesterone = resultFor(/progesterone|^p4$/i);
  const notes = valueFor("monitoringNotes", "findings");
  const next: Partial<MonitoringEntryDraft> = {};

  const fields: [keyof MonitoringEntryDraft, string][] = [
    [
      "leftSizes",
      valueFor("leftFollicleSizes", "follicleSizesLeft", "leftFollicles"),
    ],
    [
      "rightSizes",
      valueFor("rightFollicleSizes", "follicleSizesRight", "rightFollicles"),
    ],
    ["leftCount", valueFor("leftFollicleCount", "follicleCountLeft")],
    ["rightCount", valueFor("rightFollicleCount", "follicleCountRight")],
    [
      "liningThickness",
      valueFor(
        "endometrialThickness",
        "endometrialLiningThickness",
        "liningThickness",
      ),
    ],
    ["estradiol", valueFor("estradiol") || estradiol.value],
    ["estradiolUnit", valueFor("estradiolUnit") || estradiol.unit],
    ["lh", valueFor("lh") || lh.value],
    ["lhUnit", valueFor("lhUnit") || lh.unit],
    ["fsh", valueFor("fsh") || fsh.value],
    ["fshUnit", valueFor("fshUnit") || fsh.unit],
    ["progesterone", valueFor("progesterone") || progesterone.value],
    ["progesteroneUnit", valueFor("progesteroneUnit") || progesterone.unit],
    ["notes", notes],
  ];

  fields.forEach(([key, value]) => {
    if (value) next[key] = value;
  });
  return next;
}

function FieldInput({
  value,
  onChangeText,
  placeholder,
  accessibilityLabel,
  hasError = false,
  multiline = false,
  editable = true,
  keyboardType = "default",
}: {
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  accessibilityLabel: string;
  hasError?: boolean;
  multiline?: boolean;
  editable?: boolean;
  keyboardType?: "default" | "number-pad" | "decimal-pad";
}) {
  const [focused, setFocused] = useState(false);
  return (
    <MoonFieldInput
      hasError={hasError}
      focused={focused}
      paddingY={multiline ? 10 : 0}
    >
      <TextInput
        value={value}
        onChangeText={onChangeText}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        placeholderTextColor={colors.textFaint}
        accessibilityLabel={accessibilityLabel}
        editable={editable}
        keyboardType={keyboardType}
        multiline={multiline}
        numberOfLines={multiline ? 4 : undefined}
        textAlignVertical={multiline ? "top" : "center"}
        style={{
          minHeight: multiline ? 88 : 44,
          paddingHorizontal: 0,
          paddingVertical: multiline ? 0 : 12,
          color: colors.text,
          fontFamily: typography.families.sans,
          fontSize: 14,
          lineHeight: 20,
          opacity: editable ? 1 : 0.5,
        }}
      />
    </MoonFieldInput>
  );
}

function FormInput({
  label,
  hint,
  error,
  value,
  onChangeText,
  placeholder,
  accessibilityLabel,
  multiline,
  editable = true,
  keyboardType,
}: {
  label: string;
  hint?: string;
  error?: string;
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  accessibilityLabel: string;
  multiline?: boolean;
  editable?: boolean;
  keyboardType?: "default" | "number-pad" | "decimal-pad";
}) {
  return (
    <MoonFormField
      label={label}
      labelHint={hint}
      error={error}
      disabled={!editable}
    >
      <FieldInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        accessibilityLabel={accessibilityLabel}
        hasError={!!error}
        multiline={multiline}
        editable={editable}
        keyboardType={keyboardType}
      />
    </MoonFormField>
  );
}

function SummaryChip({ label, value }: { label: string; value: string }) {
  return (
    <View
      style={{
        flex: 1,
        minWidth: 92,
        borderRadius: 10,
        backgroundColor: "rgba(42,37,34,0.045)",
        borderWidth: 1,
        borderColor: "rgba(42,37,34,0.06)",
        paddingHorizontal: 10,
        paddingVertical: 8,
        gap: 2,
      }}
    >
      <Text
        variant="caption"
        style={{ color: colors.textFaint, fontSize: 10, lineHeight: 13 }}
      >
        {label}
      </Text>
      <Text
        variant="label"
        style={{ color: colors.text, fontSize: 13, lineHeight: 17 }}
      >
        {value || "—"}
      </Text>
    </View>
  );
}

function MonitoringEntryTabs({
  active,
  onChange,
}: {
  active: MonitoringSection;
  onChange: (section: MonitoringSection) => void;
}) {
  const options: FactSheetTab<MonitoringSection>[] = [
    {
      value: "ultrasound",
      label: "Ultrasound",
      icon: ScanLine,
    },
    {
      value: "bloodwork",
      label: "Bloodwork",
      icon: TestTube2,
    },
  ];
  return (
    <FactSheetTabControl tabs={options} active={active} onChange={onChange} />
  );
}

export function MonitoringEntryModal({
  open,
  dateISO,
  current,
  previous,
  onClose,
  onDateChange,
  onSave,
  onUploadReport,
}: MonitoringEntryModalProps) {
  const t = useTranslations();
  const capturePress = usePressCapture("MonitoringEntryModal");
  const { showToast } = useToast();
  const { height } = useWindowDimensions();
  const todayISO = useMemo(() => formatISODate(new Date()), []);
  const [draft, setDraft] = useState<MonitoringEntryDraft>(current);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState<MonitoringSection>("ultrasound");
  const [sourceNotice, setSourceNotice] = useState(false);
  const [sourceDocumentIds, setSourceDocumentIds] = useState<string[]>([]);
  const formScrollRef = useRef<ScrollView>(null);
  const previousOpenRef = useRef(open);
  const previousDateISORef = useRef(dateISO);

  useEffect(() => {
    const justOpened = open && !previousOpenRef.current;
    const dateChanged = dateISO !== previousDateISORef.current;
    previousOpenRef.current = open;
    previousDateISORef.current = dateISO;

    // A mutation invalidates the source query while this dialog is open. The
    // resulting `current` object may change, but it must not replace the
    // user's in-progress draft after a partial save. Reset only for a new
    // dialog session or an intentional date change.
    if (!open || (!justOpened && !dateChanged)) return;
    setDraft(current);
    setErrors({});
    setSection("ultrasound");
    setSourceNotice(false);
    setSourceDocumentIds([]);
  }, [current, dateISO, open]);

  const leftSizes = useMemo(
    () => parseNumberList(draft.leftSizes),
    [draft.leftSizes],
  );
  const rightSizes = useMemo(
    () => parseNumberList(draft.rightSizes),
    [draft.rightSizes],
  );
  const leadSize = Math.max(...leftSizes, ...rightSizes, 0);
  const leftCount =
    leftSizes.length > 0 ? String(leftSizes.length) : draft.leftCount;
  const rightCount =
    rightSizes.length > 0 ? String(rightSizes.length) : draft.rightCount;
  const previousLeftCount = previous
    ? parseNumberList(previous.leftSizes).length || previous.leftCount
    : "";
  const previousRightCount = previous
    ? parseNumberList(previous.rightSizes).length || previous.rightCount
    : "";
  const previousLeadSize = previous
    ? Math.max(
        ...parseNumberList(previous.leftSizes),
        ...parseNumberList(previous.rightSizes),
        0,
      )
    : 0;

  const update = (key: DraftKey, value: string) => {
    setDraft((previousDraft) => {
      const nextDraft = { ...previousDraft, [key]: value };
      if (
        key === "leftSizes" &&
        !value.trim() &&
        previousDraft.leftSizes.trim() &&
        previousDraft.leftCount ===
          String(parseNumberList(previousDraft.leftSizes).length)
      ) {
        nextDraft.leftCount = "";
      }
      if (
        key === "rightSizes" &&
        !value.trim() &&
        previousDraft.rightSizes.trim() &&
        previousDraft.rightCount ===
          String(parseNumberList(previousDraft.rightSizes).length)
      ) {
        nextDraft.rightCount = "";
      }
      return nextDraft;
    });
    setErrors((previousErrors) => ({
      ...previousErrors,
      [key]: undefined,
      form: undefined,
    }));
  };

  const validate = (): ValidationErrors => {
    const nextErrors: ValidationErrors = {};
    if (!dateISO) {
      nextErrors.date = t(
        "care.overview.calendar.monitoring_log.modal.date_required",
      );
    } else if (dateISO > todayISO) {
      nextErrors.date = t(
        "care.overview.calendar.monitoring_log.modal.date_future",
      );
    }
    const checks: [DraftKey, string | null][] = [
      ["leftSizes", validateSizeList(draft.leftSizes, "Left follicle sizes")],
      [
        "rightSizes",
        validateSizeList(draft.rightSizes, "Right follicle sizes"),
      ],
      ["leftCount", validateNumber(draft.leftCount, 60, "Left count")],
      ["rightCount", validateNumber(draft.rightCount, 60, "Right count")],
      [
        "liningThickness",
        validateNumber(
          draft.liningThickness,
          50,
          "Endometrial lining thickness",
        ),
      ],
      ["estradiol", validateNumber(draft.estradiol, 10000, "Estradiol")],
      ["lh", validateNumber(draft.lh, 100, "LH")],
      ["fsh", validateNumber(draft.fsh, 100, "FSH")],
      ["progesterone", validateNumber(draft.progesterone, 50, "Progesterone")],
    ];
    checks.forEach(([key, error]) => {
      if (error) nextErrors[key] = error;
    });

    const hasValue = MONITORING_VALUE_KEYS.some((key) => draft[key].trim());
    const hasExistingValue = MONITORING_VALUE_KEYS.some((key) =>
      current[key].trim(),
    );
    if (!hasValue && !hasExistingValue) {
      nextErrors.form = t(
        "care.overview.calendar.monitoring_log.modal.needs_one_value",
      );
    }
    return nextErrors;
  };

  const handleSave = async () => {
    if (saving) return;
    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      requestAnimationFrame(() => {
        if (nextErrors.date) {
          formScrollRef.current?.scrollTo({ y: 0, animated: true });
          return;
        }
        formScrollRef.current?.scrollToEnd({ animated: true });
      });
      return;
    }
    setSaving(true);
    setErrors({});
    try {
      await onSave(
        {
          ...draft,
          leftCount,
          rightCount,
        },
        sourceDocumentIds,
      );
      onClose();
    } catch {
      const message = t(
        "care.overview.calendar.monitoring_log.modal.save_failed_keep_draft",
      );
      showToast({ variant: "error", message });
    } finally {
      setSaving(false);
    }
  };

  const handleImportedMonitoring = (data: Record<string, unknown>) => {
    const imported = monitoringValuesFromOCR(data);
    setDraft((previousDraft) => {
      const nextDraft = { ...previousDraft };
      (Object.keys(imported) as DraftKey[]).forEach((key) => {
        const value = imported[key];
        if (typeof value === "string" && value && !nextDraft[key].trim()) {
          nextDraft[key] = value;
        }
      });
      return nextDraft;
    });
    setErrors((previousErrors) => ({ ...previousErrors, form: undefined }));
    setSourceNotice(true);
  };

  const handleUpload = () => {
    if (!dateISO) {
      setErrors((previousErrors) => ({
        ...previousErrors,
        date: t("care.overview.calendar.monitoring_log.modal.date_required"),
      }));
      return;
    }
    if (dateISO > todayISO) {
      setErrors((previousErrors) => ({
        ...previousErrors,
        date: t("care.overview.calendar.monitoring_log.modal.date_future"),
      }));
      return;
    }
    if (onUploadReport) {
      onUploadReport(dateISO, handleImportedMonitoring, (result) => {
        const documentId = result.documentId;
        if (!documentId) return;
        setSourceDocumentIds((currentIds) =>
          currentIds.includes(documentId)
            ? currentIds
            : [...currentIds, documentId],
        );
      });
      return;
    }
    setSourceNotice(true);
  };

  const handleDateChange = (nextDateISO: string) => {
    setSourceNotice(false);
    setErrors((previousErrors) => ({
      ...previousErrors,
      date: undefined,
      form: undefined,
    }));
    onDateChange?.(nextDateISO);
  };

  const dialogMaxHeight = Math.min(820, Math.max(520, height * 0.9));

  return (
    <Dialog
      visible={open}
      onClose={onClose}
      testID="monitoring-entry-dialog"
      closeButton="with-background"
      containerStyle={{
        width: "90%",
        maxWidth: 390,
        maxHeight: dialogMaxHeight,
      }}
      surfaceStyle={{ maxHeight: dialogMaxHeight }}
      contentStyle={{
        paddingTop: 22,
        paddingHorizontal: 22,
        paddingBottom: 18,
        maxHeight: dialogMaxHeight,
      }}
    >
      <View style={{ flex: 1, minHeight: 0 }}>
        <DialogIcon
          source={MONITORING_DIALOG_ILLUSTRATION}
          width={78}
          height={72}
          style={{ marginBottom: 16 }}
        />
        <DialogHeader
          title={t("care.overview.calendar.monitoring_log.modal.title")}
          subtitle={t("care.overview.calendar.monitoring_log.modal.subtitle")}
          style={{
            width: "100%",
            marginBottom: 16,
          }}
          titleStyle={{ textAlign: "center", width: "100%" }}
          subtitleStyle={{ textAlign: "center", width: "100%" }}
        />

        <ScrollView
          ref={formScrollRef}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          style={{ flexShrink: 1 }}
          contentContainerStyle={{ gap: 10, paddingBottom: 4 }}
        >
          <MoonDateField
            label={t(
              "care.overview.calendar.monitoring_log.modal.date_input_label",
            )}
            value={dateISO}
            onChange={handleDateChange}
            error={errors.date}
            disabled={saving}
            testID="monitoring-date-field"
          />

          {previous ? (
            <View
              style={{
                borderRadius: 14,
                backgroundColor: "rgba(255,255,255,0.42)",
                borderWidth: 1,
                borderColor: "rgba(42,37,34,0.08)",
                padding: 12,
                gap: 9,
              }}
            >
              <View style={{ flexDirection: "row", gap: 8 }}>
                <View style={{ flex: 1, gap: 2 }}>
                  <Text variant="label" style={{ fontSize: 13 }}>
                    {t(
                      "care.overview.calendar.monitoring_log.modal.previous_title",
                    )}
                  </Text>
                  <Text
                    variant="caption"
                    style={{ color: colors.textLight, fontSize: 11 }}
                  >
                    {formatDateLabel(previous.date)}
                  </Text>
                </View>
                <Info size={16} color={colors.textFaint} />
              </View>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 7 }}>
                <SummaryChip
                  label={t(
                    "care.overview.calendar.monitoring_log.field.follicles_label",
                  )}
                  value={`${previousLeftCount || "—"} · ${previousRightCount || "—"}`}
                />
                <SummaryChip
                  label={t(
                    "care.overview.calendar.monitoring_log.field.lead_size_label",
                  )}
                  value={previousLeadSize ? `${previousLeadSize} mm` : "—"}
                />
                <SummaryChip
                  label="E2"
                  value={
                    previous.estradiol
                      ? `${previous.estradiol} ${previous.estradiolUnit}`
                      : "—"
                  }
                />
              </View>
              <Text
                variant="caption"
                style={{
                  color: colors.textFaint,
                  fontSize: 11,
                  lineHeight: 15,
                }}
              >
                {t(
                  "care.overview.calendar.monitoring_log.modal.previous_detail",
                )}
              </Text>
            </View>
          ) : null}

          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
              borderRadius: 14,
              backgroundColor: "rgba(255,255,255,0.42)",
              borderWidth: 1,
              borderColor: "rgba(42,37,34,0.08)",
              padding: 12,
            }}
          >
            <View style={{ flex: 1, gap: 2 }}>
              <Text variant="label" style={{ fontSize: 13 }}>
                {t("care.overview.calendar.monitoring_log.modal.import_title")}
              </Text>
              <Text
                variant="caption"
                style={{
                  color: colors.textLight,
                  fontSize: 11,
                  lineHeight: 15,
                }}
              >
                {t("care.overview.calendar.monitoring_log.modal.import_detail")}
              </Text>
            </View>
            <Pressable
              onPress={() => {
                capturePress(
                  pressLabel(
                    t(
                      "care.overview.calendar.monitoring_log.modal.import_action",
                    ),
                  ),
                  "import_monitoring_document",
                );
                handleUpload();
              }}
              accessibilityRole="button"
              accessibilityLabel={t(
                "care.overview.calendar.monitoring_log.modal.import_action",
              )}
              style={({ pressed }) => ({
                minHeight: 32,
                borderRadius: 8,
                paddingHorizontal: 10,
                flexDirection: "row",
                alignItems: "center",
                gap: 5,
                backgroundColor: pressed
                  ? "rgba(42,37,34,0.1)"
                  : "rgba(42,37,34,0.06)",
              })}
            >
              <UploadCloud size={14} color={colors.textLight} />
              <Text
                variant="label"
                style={{ color: colors.text, fontSize: 11, lineHeight: 14 }}
              >
                {t("care.overview.calendar.monitoring_log.modal.import_action")}
              </Text>
            </Pressable>
          </View>
          {sourceNotice ? (
            <Text
              variant="caption"
              style={{ color: colors.textLight, fontSize: 11, lineHeight: 15 }}
            >
              {t("care.overview.calendar.monitoring_log.modal.import_notice")}
            </Text>
          ) : null}

          <MonitoringEntryTabs active={section} onChange={setSection} />

          {section === "ultrasound" ? (
            <View style={{ gap: 14 }}>
              <View style={{ flexDirection: "row", gap: 12 }}>
                <View style={{ flex: 1 }}>
                  <FormInput
                    label={t(
                      "care.overview.calendar.monitoring_log.modal.left_follicles",
                    )}
                    hint="mm · comma separated"
                    error={errors.leftSizes}
                    value={draft.leftSizes}
                    onChangeText={(value) => update("leftSizes", value)}
                    placeholder={t(
                      "care.overview.calendar.monitoring_log.modal.sizes_placeholder",
                    )}
                    accessibilityLabel={t(
                      "care.overview.calendar.monitoring_log.modal.left_follicles",
                    )}
                    keyboardType="decimal-pad"
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <FormInput
                    label={t(
                      "care.overview.calendar.monitoring_log.modal.right_follicles",
                    )}
                    hint="mm · comma separated"
                    error={errors.rightSizes}
                    value={draft.rightSizes}
                    onChangeText={(value) => update("rightSizes", value)}
                    placeholder={t(
                      "care.overview.calendar.monitoring_log.modal.sizes_placeholder",
                    )}
                    accessibilityLabel={t(
                      "care.overview.calendar.monitoring_log.modal.right_follicles",
                    )}
                    keyboardType="decimal-pad"
                  />
                </View>
              </View>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 7 }}>
                <SummaryChip
                  label={t(
                    "care.overview.calendar.monitoring_log.modal.derived_count_left",
                  )}
                  value={leftCount}
                />
                <SummaryChip
                  label={t(
                    "care.overview.calendar.monitoring_log.modal.derived_count_right",
                  )}
                  value={rightCount}
                />
                <SummaryChip
                  label={t(
                    "care.overview.calendar.monitoring_log.modal.derived_lead",
                  )}
                  value={leadSize ? `${leadSize} mm` : ""}
                />
              </View>
              <View style={{ flexDirection: "row", gap: 12 }}>
                <View style={{ flex: 1 }}>
                  <FormInput
                    label={t(
                      "care.overview.calendar.monitoring_log.modal.fallback_count_left",
                    )}
                    error={errors.leftCount}
                    value={draft.leftCount}
                    onChangeText={(value) => update("leftCount", value)}
                    placeholder={t(
                      "care.overview.calendar.monitoring_log.modal.count_placeholder",
                    )}
                    accessibilityLabel={t(
                      "care.overview.calendar.monitoring_log.modal.fallback_count_left",
                    )}
                    editable={leftSizes.length === 0}
                    keyboardType="number-pad"
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <FormInput
                    label={t(
                      "care.overview.calendar.monitoring_log.modal.fallback_count_right",
                    )}
                    error={errors.rightCount}
                    value={draft.rightCount}
                    onChangeText={(value) => update("rightCount", value)}
                    placeholder={t(
                      "care.overview.calendar.monitoring_log.modal.count_placeholder",
                    )}
                    accessibilityLabel={t(
                      "care.overview.calendar.monitoring_log.modal.fallback_count_right",
                    )}
                    editable={rightSizes.length === 0}
                    keyboardType="number-pad"
                  />
                </View>
              </View>
              <FormInput
                label={t(
                  "care.overview.calendar.monitoring_log.modal.lining_label",
                )}
                hint="mm"
                error={errors.liningThickness}
                value={draft.liningThickness}
                onChangeText={(value) => update("liningThickness", value)}
                placeholder={t(
                  "care.overview.calendar.monitoring_log.modal.lining_placeholder",
                )}
                accessibilityLabel={t(
                  "care.overview.calendar.monitoring_log.modal.lining_label",
                )}
                keyboardType="decimal-pad"
              />
            </View>
          ) : (
            <View style={{ gap: 14 }}>
              <BloodworkField
                label={t(
                  "care.overview.calendar.monitoring_log.modal.estradiol_label",
                )}
                value={draft.estradiol}
                unit={draft.estradiolUnit}
                options={UNIT_OPTIONS.estradiol}
                error={errors.estradiol}
                onValueChange={(value) => update("estradiol", value)}
                onUnitChange={(unit) => update("estradiolUnit", unit)}
                placeholder="e.g. 850"
              />
              <BloodworkField
                label={t(
                  "care.overview.calendar.monitoring_log.modal.lh_label",
                )}
                value={draft.lh}
                unit={draft.lhUnit}
                options={UNIT_OPTIONS.lh}
                error={errors.lh}
                onValueChange={(value) => update("lh", value)}
                onUnitChange={(unit) => update("lhUnit", unit)}
                placeholder="e.g. 4.2"
              />
              <BloodworkField
                label={t(
                  "care.overview.calendar.monitoring_log.modal.fsh_label",
                )}
                value={draft.fsh}
                unit={draft.fshUnit}
                options={UNIT_OPTIONS.fsh}
                error={errors.fsh}
                onValueChange={(value) => update("fsh", value)}
                onUnitChange={(unit) => update("fshUnit", unit)}
                placeholder="Optional"
              />
              <BloodworkField
                label={t(
                  "care.overview.calendar.monitoring_log.modal.progesterone_label",
                )}
                value={draft.progesterone}
                unit={draft.progesteroneUnit}
                options={UNIT_OPTIONS.progesterone}
                error={errors.progesterone}
                onValueChange={(value) => update("progesterone", value)}
                onUnitChange={(unit) => update("progesteroneUnit", unit)}
                placeholder="Optional"
              />
            </View>
          )}

          <FormInput
            label={t("care.overview.calendar.monitoring_log.modal.notes_label")}
            value={draft.notes}
            onChangeText={(value) => update("notes", value)}
            placeholder={t(
              "care.overview.calendar.monitoring_log.modal.notes_placeholder",
            )}
            accessibilityLabel={t(
              "care.overview.calendar.monitoring_log.modal.notes_label",
            )}
            multiline
          />

          {errors.form ? (
            <View
              accessibilityLiveRegion="assertive"
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 8,
                borderRadius: 12,
                backgroundColor: "rgba(232,146,124,0.1)",
                borderWidth: 1,
                borderColor: "rgba(232,146,124,0.25)",
                padding: 11,
              }}
            >
              <AlertCircle size={16} color={colors.coral} />
              <View style={{ flex: 1, gap: 2 }}>
                <Text
                  variant="body-sm"
                  style={{ color: colors.coral, fontSize: 12 }}
                >
                  {errors.form}
                </Text>
              </View>
            </View>
          ) : null}
        </ScrollView>

        <DialogActions
          style={{
            borderTopWidth: 1,
            borderTopColor: "rgba(42,37,34,0.08)",
            paddingTop: 14,
            marginTop: 14,
            alignItems: "stretch",
          }}
        >
          <DialogSecondaryButton
            label={t("care.overview.calendar.monitoring_log.modal.cancel")}
            onPress={onClose}
            disabled={saving}
            flex={1}
          />
          <DialogPrimaryButton
            label={t("care.overview.calendar.monitoring_log.modal.save")}
            onPress={() => void handleSave()}
            loading={saving}
            disabled={saving}
            flex={1.35}
          />
          <IconButton
            accessibilityLabel={t(
              "care.overview.calendar.monitoring_log.modal.import_action",
            )}
            onPress={handleUpload}
            disabled={saving}
            size={48}
            surface="soft"
            backgroundColor="rgba(42,37,34,0.06)"
            pressedBackgroundColor="rgba(42,37,34,0.11)"
            style={{ width: 48 }}
          >
            <UploadCloud size={18} color={colors.textLight} strokeWidth={1.8} />
          </IconButton>
        </DialogActions>
      </View>
    </Dialog>
  );
}

function BloodworkField({
  label,
  value,
  unit,
  options,
  error,
  onValueChange,
  onUnitChange,
  placeholder,
}: {
  label: string;
  value: string;
  unit: string;
  options: { label: string; value: string }[];
  error?: string;
  onValueChange: (value: string) => void;
  onUnitChange: (unit: string) => void;
  placeholder: string;
}) {
  const capturePress = usePressCapture("BloodworkField");
  const unitTriggerRef = useRef<View>(null);
  const [unitMenuOpen, setUnitMenuOpen] = useState(false);
  const [valueFocused, setValueFocused] = useState(false);
  const unitItems: AnchoredSelectMenuItem[] = options.map((option) => ({
    label: option.label,
    value: option.value,
  }));

  return (
    <MoonFormField label={label} error={error}>
      <MoonFieldInput
        hasError={!!error}
        focused={valueFocused || unitMenuOpen}
        paddingY={0}
      >
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <TextInput
            value={value}
            onChangeText={onValueChange}
            onFocus={() => setValueFocused(true)}
            onBlur={() => setValueFocused(false)}
            placeholder={placeholder}
            placeholderTextColor={colors.textFaint}
            accessibilityLabel={label}
            keyboardType="decimal-pad"
            style={{
              flex: 1,
              minHeight: 44,
              paddingHorizontal: 0,
              paddingVertical: 12,
              color: colors.text,
              fontFamily: typography.families.sans,
              fontSize: 14,
              lineHeight: 20,
            }}
          />
          <View
            style={{
              width: 1,
              height: 22,
              backgroundColor: "rgba(42,37,34,0.1)",
              marginHorizontal: 10,
            }}
          />
          <View ref={unitTriggerRef} collapsable={false}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Unit: ${unit}`}
              onPress={() => {
                capturePress(pressLabel(unit), "pick_bloodwork_unit");
                setUnitMenuOpen((open) => !open);
              }}
              style={{
                minHeight: 44,
                minWidth: 82,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 6,
              }}
            >
              <Text
                variant="body-sm"
                style={{ color: colors.text, fontSize: 14 }}
              >
                {unit}
              </Text>
              <ChevronDown
                size={16}
                color={colors.textFaint}
                strokeWidth={1.5}
              />
            </Pressable>
          </View>
        </View>
      </MoonFieldInput>
      <AnchoredSelectMenu
        visible={unitMenuOpen}
        triggerRef={unitTriggerRef}
        value={unit}
        items={unitItems}
        align="right"
        width={128}
        maxHeight={120}
        accentColor={colors.warmDark}
        onSelect={(nextUnit) => {
          onUnitChange(nextUnit);
          setUnitMenuOpen(false);
        }}
        onClose={() => setUnitMenuOpen(false)}
        testID={`monitoring-unit-menu-${label}`}
      />
    </MoonFormField>
  );
}
